import {calculateErrorMessage} from 'common/helpers'

type CachedNode = {
  element: HTMLElement
  tagName: string
  id: string | null
  classes: string[]
  attributes: Record<string, string>
  children: CachedNode[]
  parent: CachedNode | null
  depth: number
  boundingBox: DOMRect | null
}

export type DOMCache = {
  classesTree: Map<string, HTMLElement[]>
  idsTree: Map<string, HTMLElement>
  domTree: CachedNode
  buildTimestamp: number
  elementCount: number
}

export interface DOMTelemetry {
  cacheBuildTime: number
  lookupBackoffAttempts: number
  observerFallbackTriggered: boolean
  finalSuccess: boolean
  totalDuration: number
}

export interface BackoffConfig {
  initialDelay: number
  maxAttempts: number
  maxDelay: number
  multiplier: number
}

function createCachedNode(el: HTMLElement, parent: CachedNode | null, depth: number): CachedNode {
  const attrs: Record<string, string> = {}
  for (let i = 0; i < el.attributes.length; i++) {
    const a = el.attributes[i]
    attrs[a.name] = a.value
  }

  return {
    element: el,
    tagName: el.tagName,
    id: el.id || null,
    classes: Array.from(el.classList || []),
    attributes: attrs,
    children: [],
    parent,
    depth,
    boundingBox: null,
  }
}

export function buildDomCache(root: HTMLElement): DOMCache {
  const classesTree = new Map<string, HTMLElement[]>()
  const idsTree = new Map<string, HTMLElement>()
  let elementCount = 0

  const rootNode = createCachedNode(root, null, 0)

  function traverse(el: HTMLElement, parentNode: CachedNode, depth: number) {
    const node = createCachedNode(el, parentNode, depth)
    parentNode.children.push(node)

    node.classes.forEach((cls) => {
      if (!classesTree.has(cls)) classesTree.set(cls, [])
      classesTree.get(cls)!.push(el)
    })
    if (node.id) {
      idsTree.set(node.id, el)
    }
    elementCount++
    Array.from(el.children).forEach((child) => {
      if (child instanceof HTMLElement) traverse(child, node, depth + 1)
    })
  }

  Array.from(root.children).forEach((child) => {
    if (child instanceof HTMLElement) traverse(child, rootNode, 1)
  })

  function computeBoundingBoxes(node: CachedNode) {
    try {
      node.boundingBox = node.element.getBoundingClientRect()
    } catch (e) {
      calculateErrorMessage(e)
      node.boundingBox = null
    }
    node.children.forEach(computeBoundingBoxes)
  }
  computeBoundingBoxes(rootNode)

  return {
    classesTree,
    idsTree,
    domTree: rootNode,
    buildTimestamp: Date.now(),
    elementCount,
  }
}

export function refreshBoundingBoxes(cache: DOMCache) {
  if (!cache || !cache.domTree) return
  function walk(node: CachedNode) {
    try {
      node.boundingBox = node.element.getBoundingClientRect()
    } catch (e) {
      calculateErrorMessage(e)
      node.boundingBox = null
    }
    node.children.forEach(walk)
  }
  walk(cache.domTree)
  cache.buildTimestamp = Date.now()
}

export async function documentReadyHelper(): Promise<void> {
  if (document.readyState === 'loading') {
    await new Promise<void>((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(), {
        once: true,
      })
    })
  }

  if (document.readyState !== 'complete') {
    await new Promise<void>((resolve) => {
      window.addEventListener('load', () => resolve(), {once: true})
    })
  }

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })

  await new Promise<void>((resolve) => setTimeout(resolve, 150))
}

export async function findWithBackoff(
  selector: string,
  config: Partial<BackoffConfig> = {}
): Promise<{element: HTMLElement | null; attempts: number; duration: number}> {
  const {initialDelay = 100, maxAttempts = 3, maxDelay = 400, multiplier = 2} = config

  const startTime = Date.now()
  let delay = initialDelay

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const element = document.querySelector(selector) as HTMLElement | null

    if (element) {
      const duration = Date.now() - startTime
      return {element, attempts: attempt, duration}
    }

    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delay))
      delay = Math.min(delay * multiplier, maxDelay)
    }
  }

  const duration = Date.now() - startTime
  return {element: null, attempts: maxAttempts, duration}
}

export async function findWithObserverFallback(
  selector: string,
  root: HTMLElement = document.body,
  timeout: number = 10000
): Promise<{
  element: HTMLElement | null
  usedObserver: boolean
  duration: number
}> {
  const startTime = Date.now()

  let element = root.querySelector(selector) as HTMLElement | null
  if (element) {
    return {element, usedObserver: false, duration: Date.now() - startTime}
  }

  return new Promise((resolve) => {
    let observer: MutationObserver | null = null
    let timeoutId: NodeJS.Timeout | null = null
    let resolved = false

    const cleanup = () => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const resolveOnce = (el: HTMLElement | null, usedObserver: boolean) => {
      if (resolved) return
      resolved = true
      cleanup()
      const duration = Date.now() - startTime
      resolve({element: el, usedObserver, duration})
    }

    observer = new MutationObserver(() => {
      element = root.querySelector(selector) as HTMLElement | null
      if (element) {
        resolveOnce(element, true)
      }
    })

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    })

    timeoutId = setTimeout(() => {
      resolveOnce(null, true)
    }, timeout)

    element = root.querySelector(selector) as HTMLElement | null
    if (element) {
      resolveOnce(element, false)
    }
  })
}

export async function findElementRobust(
  selector: string,
  options: {
    backoffConfig?: Partial<BackoffConfig>
    observerTimeout?: number
    observerRoot?: HTMLElement
  } = {}
): Promise<{
  element: HTMLElement | null
  telemetry: DOMTelemetry
}> {
  const startTime = Date.now()
  let backoffAttempts = 0
  let observerTriggered = false

  const backoffResult = await findWithBackoff(selector, options.backoffConfig)
  backoffAttempts = backoffResult.attempts

  if (backoffResult.element) {
    return {
      element: backoffResult.element,
      telemetry: {
        cacheBuildTime: 0,
        lookupBackoffAttempts: backoffAttempts,
        observerFallbackTriggered: false,
        finalSuccess: true,
        totalDuration: Date.now() - startTime,
      },
    }
  }

  observerTriggered = true

  const observerResult = await findWithObserverFallback(
    selector,
    options.observerRoot || document.body,
    options.observerTimeout || 10000
  )

  return {
    element: observerResult.element,
    telemetry: {
      cacheBuildTime: 0,
      lookupBackoffAttempts: backoffAttempts,
      observerFallbackTriggered: observerTriggered,
      finalSuccess: !!observerResult.element,
      totalDuration: Date.now() - startTime,
    },
  }
}

export default {
  buildDomCache,
  refreshBoundingBoxes,
  documentReadyHelper,
  findWithBackoff,
  findWithObserverFallback,
  findElementRobust,
}
